import { TIPO_INSPECAO } from "@/contexts/cadastro/estabelecimento";
import { useTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/hooks/useTermoNotificacao";
import { ParecerTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/model/ParecerTermoNotificacao";
import { PageLoader } from "@/shared/components/PageLoader/PageLoader";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Grid,
  Input,
  List,
  Radio,
  Result,
  Row,
  Space,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

interface FormValues {
  justificativaParecer: string;
  dataParecer: Dayjs;
  parecerFavoravel: boolean;
  prazoFinal?: Dayjs;
}

export function NovoParecer() {
  const screens = Grid.useBreakpoint();
  const { termoId } = useParams<{ termoId: string }>();
  const navigate = useNavigate();
  const {
    getTermoNotificacaoSaneamentoApi,
    submitParecerTermoNotificacaoApi,
    getAnexoApi,
  } = useTermoNotificacao();
  const { notification, message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const parecerFavoravel = Form.useWatch("parecerFavoravel", form);

  const {
    data: termoNotificacao,
    isLoading: loadingTermo,
    error: errorTermo,
    refetch,
  } = useQuery({
    queryKey: ["TERMO_NOTIFICACAO", termoId],
    enabled: !!termoId,
    queryFn: () => {
      return getTermoNotificacaoSaneamentoApi(Number(termoId));
    },
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);
      message.loading({
        content: "Salvando Parecer do termo...",
        key: "parecer-termo",
        duration: 0,
      });

      if (!termoNotificacao || !termoNotificacao.saneamentos) {
        throw new Error("Termo de notificação não encontrado.");
      }

      const request: ParecerTermoNotificacao = {
        dataParecer: values.dataParecer.format("YYYY-MM-DDTHH:mm:ss"),
        justificativaParecer: values.justificativaParecer || "",
        situacao: values.parecerFavoravel ? "FAVORAVEL" : "DESFAVORAVEL",
        prazoFinal: values.prazoFinal?.format("YYYY-MM-DD"),
      };

      const ultimoSaneamento =
        termoNotificacao.saneamentos[termoNotificacao.saneamentos.length - 1];

      await submitParecerTermoNotificacaoApi(
        Number(termoId),
        ultimoSaneamento.id,
        request
      );

      message.destroy("parecer-termo");
      setIsSubmitting(false);
      notification.success({
        message: "Parecer do termo salvo com sucesso",
      });

      handleBack();
    } catch (error) {
      console.error("Erro ao enviar o parecer do termo:", error);
      setIsSubmitting(false);
      message.destroy("parecer-termo");
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível salvar o parecer do termo",
        description: messageError,
      });
    }
  };

  const handleOpenAnexo = async (nomeUpload?: string) => {
    if (!nomeUpload) return;

    try {
      const anexoBlob = await getAnexoApi(nomeUpload);
      saveAs(anexoBlob, nomeUpload || "anexo");
    } catch (error) {
      message.error("Não foi possível abrir o anexo.");
    }
  };

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Novo Parecer do Plano de Ação do Termo de Notificação";
  }, []);

  if (loadingTermo) return <PageLoader />;

  if (errorTermo) {
    return (
      <Result
        status={500}
        title="Parece que aconteceu algum problema com o servidor."
        subTitle={formatMessageError(errorTermo)}
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Voltar
            </Button>
            <Button onClick={() => refetch()}>Tentar Novamente</Button>
          </Space>
        }
      />
    );
  }

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Parecer do Plano de Ação do Termo de Notificação{" "}
          {termoNotificacao?.numero}
        </Typography.Title>
      </Space>
      <Divider />

      <Form layout="vertical">
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12}>
            <Form.Item label="Estabelecimento">
              <Input
                disabled
                value={`${termoNotificacao?.estabelecimento.numeroInspecao} - ${termoNotificacao?.estabelecimento ? TIPO_INSPECAO[termoNotificacao.estabelecimento.tipoInspecao] : "SISE"} - ${termoNotificacao?.estabelecimento.nome}`}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Teor da Notificação">
              <Input.TextArea
                rows={4}
                disabled
                value={termoNotificacao?.teor}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Motivo" rules={[{ required: true }]}>
              <Input
                disabled
                value={termoNotificacao?.motivos
                  .map((motivo) => motivo.nome?.toUpperCase())
                  .join(", ")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item colon={false} label="Exige Plano de Ação?">
              <Radio.Group disabled value={termoNotificacao?.exigeSaneamento}>
                <Radio value={true}>Sim</Radio>
                <Radio value={false}>Não</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Prazo para atendimento">
              <DatePicker
                format={"DD/MM/YYYY"}
                disabled
                value={
                  termoNotificacao?.prazoAtendimento
                    ? dayjs(termoNotificacao.prazoAtendimento)
                    : undefined
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider orientation="left">
        <Typography.Title level={5}>Parecer</Typography.Title>
      </Divider>

      {termoNotificacao?.saneamentos?.length === 0 && (
        <Typography.Text type="secondary">
          Nenhum Plano de Ação registrado para este termo de notificação até
          agora.
        </Typography.Text>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {termoNotificacao?.saneamentos?.map((item) => (
          <Col xs={24} key={item.id}>
            <Card
              title={`Plano de Ação - ${dayjs(item.dataSaneamento).format("DD/MM/YYYY")}`}
              style={{ marginBottom: 16, backgroundColor: "#f6f8fa" }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Typography.Text strong>Descrição:</Typography.Text>
                  <Typography.Paragraph>{item.descricao}</Typography.Paragraph>
                </Col>

                <Col xs={24} sm={12}>
                  <Typography.Text strong>Registrado por:</Typography.Text>
                  <Typography.Paragraph>
                    {item.usuarioSaneamento?.nome}
                  </Typography.Paragraph>
                </Col>

                {item.anexos.length > 0 && (
                  <Col xs={24}>
                    <Typography.Text strong>Anexos:</Typography.Text>
                    <List
                      dataSource={item.anexos}
                      renderItem={(anexo) => (
                        <List.Item>
                          <Button
                            type="link"
                            onClick={() => handleOpenAnexo(anexo.nomeUpload)}
                          >
                            {anexo.nomeArquivo}
                          </Button>
                        </List.Item>
                      )}
                    />
                  </Col>
                )}

                {item.dataParecer && (
                  <>
                    <Divider orientation="left" orientationMargin={0}>
                      <Typography.Title level={5}>
                        Parecer - {dayjs(item.dataParecer).format("DD/MM/YYYY")}
                      </Typography.Title>
                    </Divider>

                    <Col xs={24} sm={12}>
                      <Typography.Text strong>
                        Justificativa Parecer:
                      </Typography.Text>
                      <Typography.Paragraph>
                        {item.justificativaParecer}
                      </Typography.Paragraph>
                    </Col>

                    <Col xs={24} sm={12}>
                      <Typography.Text strong>Registrado por:</Typography.Text>
                      <Typography.Paragraph>
                        {item.usuarioParecer?.nome}
                      </Typography.Paragraph>
                    </Col>

                    <Col xs={24} sm={12}>
                      <Typography.Text strong>Situação:</Typography.Text>
                      <Typography.Paragraph>
                        {item.situacao}
                      </Typography.Paragraph>
                    </Col>

                    {item.prazoFinal && (
                      <Col xs={24} sm={12}>
                        <Typography.Text strong>Prazo Final:</Typography.Text>
                        <Typography.Paragraph>
                          {dayjs(item.prazoFinal).format("DD/MM/YYYY")}
                        </Typography.Paragraph>
                      </Col>
                    )}
                  </>
                )}
              </Row>
            </Card>
          </Col>
        ))}
      </Row>

      <Divider />

      <Form
        id="form-parecer-termo"
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          dataParecer: dayjs(),
        }}
      >
        <Row gutter={[16, 8]}>
          <Col xs={24}>
            <Form.Item
              name="parecerFavoravel"
              valuePropName="checked"
              label="Parecer"
              tooltip="Marque se o parecer for favorável ao plano de ação."
            >
              <Checkbox>Favorável</Checkbox>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              name="dataParecer"
              label="Data Parecer"
              rules={[{ required: true }]}
            >
              <DatePicker format={"DD/MM/YYYY"} style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {!parecerFavoravel && (
            <Col xs={24} md={8}>
              <Form.Item name="prazoFinal" label="Prazo Final">
                <DatePicker format={"DD/MM/YYYY"} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          )}

          <Col span={24}>
            <Form.Item
              name="justificativaParecer"
              label="Justificativa Parecer"
              rules={[{ required: !parecerFavoravel }]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Informe a justificativa do parecer"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row justify={"center"} gutter={[10, 10]}>
          <Col>
            <Button
              htmlType="button"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ width: 200 }}
              disabled={isSubmitting}
            >
              Voltar
            </Button>
          </Col>

          <Col>
            <Button
              form="form-parecer-termo"
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              style={{ width: 200 }}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Salvar
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}
